import './Sidebar.css';

import {
  IonAccordion,
  IonAccordionGroup,
  IonIcon,
  IonItem,
  IonLabel,
} from '@ionic/react';
import { closeOutline, searchOutline } from 'ionicons/icons';
import { useHistory, useLocation } from 'react-router';
import { KeyboardEvent, useContext, useLayoutEffect, useRef } from 'react';
import useRoutes from '../../hooks/useRoutes';
import MenuSeparator from '../Manager/MenuSeparator/MenuSeparator';
import { GlobalLocationStateType, MenuLeaf } from '../../types/types';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useMenuSearch } from './hooks/useMenuSearch';
import { useActiveMenu } from './hooks/useActiveMenu';
import { useMenuScroll } from './hooks/useMenuScroll';
import { useDatasetSessionStorageContext } from '../../hooks/context/useDatasetSessionStorageContext';
import { DatasetContext } from '../../context/DatasetContext/DatasetContext';
import { highlightMatch } from '../../utils/highlightMatch';
import { getFirstMenuLeaf } from './utils/getFirstMenuLeaf';
import { getMenuLeafTarget } from './utils/getMenuLeafTarget';
import { getMenuLeafKey } from './utils/getMenuLeafKey';
import { isTrailingSeparator } from './utils/isTrailingSeparator';
import Input from '../Input/Input';

interface SidebarProps {
  menuItems: any[];
  peersets?: any[];
  onMenuItemClick?: (url: string) => void;
}

const Sidebar = ({ menuItems, peersets, onMenuItemClick }: SidebarProps) => {
  const routes = useRoutes();
  const { t } = useTranslate();
  const history = useHistory();
  const { pathname } = useLocation<GlobalLocationStateType>();
  const { setDatasetSessionStorageState } = useDatasetSessionStorageContext();
  // Raw useContext (not the throwing accessor): Router's hasError/isPending
  // branches render PageLayout → Sidebar OUTSIDE DatasetContextProvider
  // (LoggedInLayout), so the accessor would crash the connection-error page.
  const { clearDatasetControl, isProvided: isDatasetContextProvided } =
    useContext(DatasetContext);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const {
    openMenu,
    sidebarLoaded,
    selectedItem,
    toggleOpenMenu,
    toggleOpenSubmenu,
    openActiveMenu,
  } = useActiveMenu({ menuItems, peersets });

  const {
    searchQuery,
    setSearchQuery,
    resetSearch,
    isSearching,
    overviewMatches,
    filteredMenuItems,
    openBlockKeys,
    openSubmenuKeys,
    onSearchBlocksChange,
    onSearchSubmenusChange,
    searchInputRef,
  } = useMenuSearch({
    menuItems,
    openMenu,
    scopeRef: containerRef,
    activateAnywhere: pathname.startsWith(routes.dashboards()),
  });

  // Keeps the active item centered after the menu settles — on every redirect, on reload, and as the
  // search filters — so navigation always lands on the active item.
  useMenuScroll({
    scrollRef: menuRef,
    ready: sidebarLoaded,
    selectedItem,
    searchQuery,
  });

  // Resets the search after any item that redirects, so the menu is back to its full state on the next visit.
  const handleMenuItemClick = (url: string) => {
    // Navigating from a search result (Enter or clicking a filtered item) should land centered on it,
    // even across the redirect's remount — flag a one-shot center intent that useMenuScroll consumes.
    if (isSearching) {
      setDatasetSessionStorageState({ menuScrollCenterOnce: true });
    }
    // Menu navigation always cancels the dataset control data — even when the
    // click targets the dataset the data belongs to.
    if (isDatasetContextProvided) {
      clearDatasetControl(url);
    }
    resetSearch();
    onMenuItemClick?.(url);
  };

  // Form leaves push programmatically to carry menuFromUrl (close/back target); dataset leaves use Ionic routerLink.
  const getMenuItemNavProps = (
    menuItem: MenuLeaf,
    target: { routerLink: string; navKey: string }
  ): { routerLink?: string; onClick: () => void } => {
    if (menuItem.formId) {
      return {
        onClick: () => {
          handleMenuItemClick(target.navKey);
          history.push(target.routerLink, { menuFromUrl: pathname });
        },
      };
    }

    return {
      routerLink: target.routerLink,
      onClick: () => handleMenuItemClick(target.navKey),
    };
  };

  // Enter in the search input navigates to the first available result (the overview entry takes priority as it renders first).
  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const isEnter = event.key === 'Enter' || event.code === 'NumpadEnter';
    if (!isEnter || !isSearching) {
      return;
    }

    if (overviewMatches) {
      history.push(routes.dashboards());
      handleMenuItemClick('dashboards');
      // Confirming a search hands the keyboard over to the redirect target, so the input lets go of focus.
      searchInputRef.current?.blur();
      return;
    }

    const firstLeaf = getFirstMenuLeaf(filteredMenuItems);
    const target = firstLeaf ? getMenuLeafTarget(firstLeaf, routes) : null;
    if (!target) {
      return;
    }

    handleMenuItemClick(target.navKey);
    history.push(
      target.routerLink,
      firstLeaf?.formId ? { menuFromUrl: pathname } : undefined
    );
    // Confirming a search hands the keyboard over to the redirect target, so the input lets go of focus.
    searchInputRef.current?.blur();
  };

  // Drop any manual expand/collapse override as soon as searching starts. While searching the open state
  // is driven by the filter anyway, and this guarantees that when the search is cleared the menu shows
  // the active item's block already open — no stale "collapsed" frame that would desync Ionic's accordion.
  useLayoutEffect(() => {
    if (isSearching) {
      openActiveMenu();
    }
  }, [isSearching, openActiveMenu]);

  return (
    <div className="sidebar-container" ref={containerRef}>
      <div className="sidebar-menu" ref={menuRef}>
        {sidebarLoaded && (
          <>
            {overviewMatches && (
              <IonItem
                button
                data-menu-url="dashboards"
                color={
                  pathname.startsWith(routes.dashboards())
                    ? 'primary'
                    : undefined
                }
                lines="none"
                onClick={() => handleMenuItemClick('dashboards')}
                routerLink={routes.dashboards()}
              >
                <img
                  className="menu-icon"
                  src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAtOTYwIDk2MCA5NjAiIHdpZHRoPSIyNHB4IiBmaWxsPSIjMzgzNzQwIj48cGF0aCBkPSJNMjk1LTExOXEtMzYtMS02OC41LTE4LjVUMTY1LTE4OXEtNDAtNDgtNjIuNS0xMTQuNVQ4MC00NDBxMC04MyAzMS41LTE1NlQxOTctNzIzcTU0LTU0IDEyNy04NS41VDQ4MC04NDBxODMgMCAxNTYgMzJ0MTI3IDg3cTU0IDU1IDg1LjUgMTI5VDg4MC00MzNxMCA3Ny0yNSAxNDR0LTcxIDExM3EtMjggMjgtNTkgNDIuNVQ2NjItMTE5cS0xOCAwLTM2LTQuNVQ1OTAtMTM3bC01Ni0yOHEtMTItNi0yNS41LTl0LTI4LjUtM3EtMTUgMC0yOC41IDN0LTI1LjUgOWwtNTYgMjhxLTE5IDEwLTM3LjUgMTQuNVQyOTUtMTE5Wm0yLTgwcTkgMCAxOC41LTJ0MTguNS03bDU2LTI4cTIxLTExIDQzLjUtMTZ0NDUuNS01cTIzIDAgNDYgNXQ0NCAxNmw1NyAyOHE5IDUgMTggN3QxOCAycTE5IDAgMzYtMTB0MzQtMzBxMzItMzggNTAtOTF0MTgtMTA5cTAtMTM0LTkzLTIyNy41VDQ4MC03NjBxLTEzNCAwLTIyNyA5NHQtOTMgMjI4cTAgNTcgMTguNSAxMTF0NTEuNSA5MXExNyAyMCAzMyAyOC41dDM0IDguNVptMTgzLTI4MVptMCAxMjBxMzMgMCA1Ni41LTIzLjVUNTYwLTQ0MHEwLTgtMS41LTE2dC00LjUtMTZsNTAtNjdxMTAgMTMgMTcuNSAyNy41VDYzNC00ODBoODJxLTE1LTg4LTgxLjUtMTQ0VDQ4MC02ODBxLTg4IDAtMTU1IDU2LjVUMjQ0LTQ4MGg4MnExNC01NCA1Ny04N3Q5Ny0zM3ExNyAwIDMyIDN0MjkgOWwtNTEgNjlxLTIgMC01LS41dC01LS41cS0zMyAwLTU2LjUgMjMuNVQ0MDAtNDQwcTAgMzMgMjMuNSA1Ni41VDQ4MC0zNjBaIi8+PC9zdmc+"
                />
                <IonLabel>{highlightMatch(t('overview'), searchQuery)}</IonLabel>
              </IonItem>
            )}
            <IonAccordionGroup
              multiple
              value={openBlockKeys}
              onIonChange={onSearchBlocksChange}
            >
              {filteredMenuItems.map((menuBlock) => (
                <IonAccordion
                  value={menuBlock.key}
                  key={menuBlock.key}
                  id={`menu_block_${menuBlock.key}`}
                >
                  <IonItem
                    slot="header"
                    lines="none"
                    onClick={() => {
                      // While searching, open state is controlled via onIonChange; the route-active override must stay untouched.
                      if (!isSearching) {
                        toggleOpenMenu(menuBlock.key);
                      }
                    }}
                  >
                    {menuBlock.icon && (
                      <img src={menuBlock.icon} className="menu-icon" />
                    )}
                    <IonLabel className="top-leve-menu">
                      {highlightMatch(menuBlock.label, searchQuery)}
                    </IonLabel>
                  </IonItem>
                  <div slot="content">
                    <IonAccordionGroup
                      multiple
                      value={openSubmenuKeys}
                      onIonChange={onSearchSubmenusChange}
                    >
                      {(menuBlock.children ?? []).map((menuItem, index) => {
                        if (
                          isTrailingSeparator(menuBlock.children ?? [], index)
                        ) {
                          return null;
                        }
                        if (menuItem.type === 'submenu' && menuItem.children) {
                          const submenuChildren = menuItem.children;
                          return (
                            <IonAccordion
                              value={menuItem.key}
                              key={menuItem.key}
                            >
                              <IonItem
                                slot="header"
                                lines="none"
                                onClick={() => {
                                  if (!isSearching) {
                                    toggleOpenSubmenu(menuItem.key);
                                  }
                                }}
                              >
                                <IonLabel>
                                  {highlightMatch(menuItem.label, searchQuery)}
                                </IonLabel>
                              </IonItem>
                              <div slot="content">
                                {submenuChildren.map(
                                  (submenuItem, submenuItemIndex) => {
                                    if (
                                      isTrailingSeparator(
                                        submenuChildren,
                                        submenuItemIndex
                                      )
                                    ) {
                                      return null;
                                    }
                                    if (submenuItem.type === 'separator') {
                                      return (
                                        <MenuSeparator
                                          key={submenuItemIndex}
                                          label={submenuItem.label}
                                        />
                                      );
                                    }
                                    const target = getMenuLeafTarget(
                                      submenuItem,
                                      routes
                                    );
                                    const isSelected =
                                      getMenuLeafKey(submenuItem) ===
                                      selectedItem;
                                    return (
                                      <IonItem
                                        key={submenuItemIndex}
                                        button
                                        data-menu-url={getMenuLeafKey(
                                          submenuItem
                                        )}
                                        {...(target &&
                                          getMenuItemNavProps(
                                            submenuItem,
                                            target
                                          ))}
                                        color={
                                          isSelected ? 'primary' : undefined
                                        }
                                        lines="none"
                                      >
                                        <IonLabel>
                                          {highlightMatch(
                                            submenuItem.label,
                                            searchQuery
                                          )}
                                        </IonLabel>
                                      </IonItem>
                                    );
                                  }
                                )}
                              </div>
                            </IonAccordion>
                          );
                        }
                        if (menuItem.type === 'separator') {
                          return (
                            <MenuSeparator key={index} label={menuItem.label} />
                          );
                        }
                        const target = getMenuLeafTarget(menuItem, routes);
                        const isSelected =
                          getMenuLeafKey(menuItem) === selectedItem;
                        return (
                          <IonItem
                            key={index}
                            button
                            data-menu-url={getMenuLeafKey(menuItem)}
                            {...(target &&
                              getMenuItemNavProps(menuItem, target))}
                            color={isSelected ? 'primary' : undefined}
                            lines="none"
                          >
                            <IonLabel>
                              {highlightMatch(menuItem.label, searchQuery)}
                            </IonLabel>
                          </IonItem>
                        );
                      })}
                    </IonAccordionGroup>
                  </div>
                </IonAccordion>
              ))}
            </IonAccordionGroup>
            {isSearching &&
              filteredMenuItems.length === 0 &&
              !overviewMatches && (
                <div className="sidebar-no-results">
                  {t('noMenuItemsFound')}
                </div>
              )}
          </>
        )}
      </div>
      <div className="sidebar-search-footer">
        <Input
          name="search"
          placeholder={t('search')}
          icon={searchOutline}
          value={searchQuery}
          onChange={setSearchQuery}
          onKeyDown={onSearchKeyDown}
          inputRef={searchInputRef}
          hideErrorText
        />
        {isSearching && (
          <button
            type="button"
            className="sidebar-search-clear"
            aria-label={t('clear')}
            onClick={() => {
              setSearchQuery('');
              searchInputRef.current?.focus();
            }}
          >
            <IonIcon icon={closeOutline} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
